/**
 * The app's icon set: Phosphor glyphs wrapped as MUI SvgIcons.
 *
 * This is the ONLY file that may import @phosphor-icons/react (enforced by
 * lint). Exports are named after the @mui/icons-material icons they replace,
 * so migrating a call site is purely an import rewrite — no JSX changes.
 * The full set is mapped up front so codemod batches (PRs 2..n) carry no
 * mapping decisions and can land in parallel. The "Icons/Phosphor parity"
 * story renders every pair side by side for visual QA.
 *
 * Import each glyph from its deep path, never the package barrel — the barrel
 * re-exports ~1,500 modules, which dev mode and vitest load eagerly (the
 * barrel import measurably slowed every theme-importing test to timeout).
 *
 * Weights are decided here, once, never at call sites:
 *   - filled Material icon  -> "fill"
 *   - outlined Material icon -> "regular" (Phosphor's default)
 */
import { AndroidLogoIcon as PhAndroidLogo } from "@phosphor-icons/react/dist/csr/AndroidLogo";
import { AppleLogoIcon as PhAppleLogo } from "@phosphor-icons/react/dist/csr/AppleLogo";
import { AppWindowIcon as PhAppWindow } from "@phosphor-icons/react/dist/csr/AppWindow";
import { ArchiveIcon as PhArchive } from "@phosphor-icons/react/dist/csr/Archive";
import { ArrowClockwiseIcon as PhArrowClockwise } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { ArrowCounterClockwiseIcon as PhArrowCounterClockwise } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { ArrowDownIcon as PhArrowDown } from "@phosphor-icons/react/dist/csr/ArrowDown";
import { ArrowLeftIcon as PhArrowLeft } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { ArrowRightIcon as PhArrowRight } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { ArrowsClockwiseIcon as PhArrowsClockwise } from "@phosphor-icons/react/dist/csr/ArrowsClockwise";
import { ArrowsInLineVerticalIcon as PhArrowsInLineVertical } from "@phosphor-icons/react/dist/csr/ArrowsInLineVertical";
import { ArrowsOutLineVerticalIcon as PhArrowsOutLineVertical } from "@phosphor-icons/react/dist/csr/ArrowsOutLineVertical";
import { ArrowSquareOutIcon as PhArrowSquareOut } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { ArrowsSplitIcon as PhArrowsSplit } from "@phosphor-icons/react/dist/csr/ArrowsSplit";
import { ArrowUpIcon as PhArrowUp } from "@phosphor-icons/react/dist/csr/ArrowUp";
import { ArticleIcon as PhArticle } from "@phosphor-icons/react/dist/csr/Article";
import { BellIcon as PhBell } from "@phosphor-icons/react/dist/csr/Bell";
import { BlueprintIcon as PhBlueprint } from "@phosphor-icons/react/dist/csr/Blueprint";
import { BookIcon as PhBook } from "@phosphor-icons/react/dist/csr/Book";
import { BookmarkSimpleIcon as PhBookmarkSimple } from "@phosphor-icons/react/dist/csr/BookmarkSimple";
import { BooksIcon as PhBooks } from "@phosphor-icons/react/dist/csr/Books";
import { BoundingBoxIcon as PhBoundingBox } from "@phosphor-icons/react/dist/csr/BoundingBox";
import { BracketsCurlyIcon as PhBracketsCurly } from "@phosphor-icons/react/dist/csr/BracketsCurly";
import { CalendarBlankIcon as PhCalendarBlank } from "@phosphor-icons/react/dist/csr/CalendarBlank";
import { CalendarDotsIcon as PhCalendarDots } from "@phosphor-icons/react/dist/csr/CalendarDots";
import { CaretDownIcon as PhCaretDown } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretLeftIcon as PhCaretLeft } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretLineLeftIcon as PhCaretLineLeft } from "@phosphor-icons/react/dist/csr/CaretLineLeft";
import { CaretLineRightIcon as PhCaretLineRight } from "@phosphor-icons/react/dist/csr/CaretLineRight";
import { CaretRightIcon as PhCaretRight } from "@phosphor-icons/react/dist/csr/CaretRight";
import { CaretUpIcon as PhCaretUp } from "@phosphor-icons/react/dist/csr/CaretUp";
import { ChartBarIcon as PhChartBar } from "@phosphor-icons/react/dist/csr/ChartBar";
import { ChatDotsIcon as PhChatDots } from "@phosphor-icons/react/dist/csr/ChatDots";
import { CheckIcon as PhCheck } from "@phosphor-icons/react/dist/csr/Check";
import { CheckCircleIcon as PhCheckCircle } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { CheckSquareIcon as PhCheckSquare } from "@phosphor-icons/react/dist/csr/CheckSquare";
import { CircleIcon as PhCircle } from "@phosphor-icons/react/dist/csr/Circle";
import { ClipboardIcon as PhClipboard } from "@phosphor-icons/react/dist/csr/Clipboard";
import { ClockIcon as PhClock } from "@phosphor-icons/react/dist/csr/Clock";
import { ClockCounterClockwiseIcon as PhClockCounterClockwise } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { CloudArrowUpIcon as PhCloudArrowUp } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { CodeIcon as PhCode } from "@phosphor-icons/react/dist/csr/Code";
import { ColumnsIcon as PhColumns } from "@phosphor-icons/react/dist/csr/Columns";
import { CopyIcon as PhCopy } from "@phosphor-icons/react/dist/csr/Copy";
import { CrownIcon as PhCrown } from "@phosphor-icons/react/dist/csr/Crown";
import { CurrencyDollarIcon as PhCurrencyDollar } from "@phosphor-icons/react/dist/csr/CurrencyDollar";
import { CursorClickIcon as PhCursorClick } from "@phosphor-icons/react/dist/csr/CursorClick";
import { CursorTextIcon as PhCursorText } from "@phosphor-icons/react/dist/csr/CursorText";
import { DeviceMobileIcon as PhDeviceMobile } from "@phosphor-icons/react/dist/csr/DeviceMobile";
import { DiceFiveIcon as PhDiceFive } from "@phosphor-icons/react/dist/csr/DiceFive";
import { DotsNineIcon as PhDotsNine } from "@phosphor-icons/react/dist/csr/DotsNine";
import { DotsSixVerticalIcon as PhDotsSixVertical } from "@phosphor-icons/react/dist/csr/DotsSixVertical";
import { DotsThreeVerticalIcon as PhDotsThreeVertical } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { DownloadSimpleIcon as PhDownloadSimple } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { EnvelopeOpenIcon as PhEnvelopeOpen } from "@phosphor-icons/react/dist/csr/EnvelopeOpen";
import { EnvelopeSimpleIcon as PhEnvelopeSimple } from "@phosphor-icons/react/dist/csr/EnvelopeSimple";
import { EyeIcon as PhEye } from "@phosphor-icons/react/dist/csr/Eye";
import { EyedropperIcon as PhEyedropper } from "@phosphor-icons/react/dist/csr/Eyedropper";
import { EyeSlashIcon as PhEyeSlash } from "@phosphor-icons/react/dist/csr/EyeSlash";
import { FadersHorizontalIcon as PhFadersHorizontal } from "@phosphor-icons/react/dist/csr/FadersHorizontal";
import { FileIcon as PhFile } from "@phosphor-icons/react/dist/csr/File";
import { FileTextIcon as PhFileText } from "@phosphor-icons/react/dist/csr/FileText";
import { FireIcon as PhFire } from "@phosphor-icons/react/dist/csr/Fire";
import { FlaskIcon as PhFlask } from "@phosphor-icons/react/dist/csr/Flask";
import { FloppyDiskIcon as PhFloppyDisk } from "@phosphor-icons/react/dist/csr/FloppyDisk";
import { FrameCornersIcon as PhFrameCorners } from "@phosphor-icons/react/dist/csr/FrameCorners";
import { GearIcon as PhGear } from "@phosphor-icons/react/dist/csr/Gear";
import { GlobeIcon as PhGlobe } from "@phosphor-icons/react/dist/csr/Globe";
import { GoogleLogoIcon as PhGoogleLogo } from "@phosphor-icons/react/dist/csr/GoogleLogo";
import { GridFourIcon as PhGridFour } from "@phosphor-icons/react/dist/csr/GridFour";
import { HeartIcon as PhHeart } from "@phosphor-icons/react/dist/csr/Heart";
import { HexagonIcon as PhHexagon } from "@phosphor-icons/react/dist/csr/Hexagon";
import { HouseIcon as PhHouse } from "@phosphor-icons/react/dist/csr/House";
import { ImageIcon as PhImage } from "@phosphor-icons/react/dist/csr/Image";
import { ImageBrokenIcon as PhImageBroken } from "@phosphor-icons/react/dist/csr/ImageBroken";
import { ImageSquareIcon as PhImageSquare } from "@phosphor-icons/react/dist/csr/ImageSquare";
import { InfoIcon as PhInfo } from "@phosphor-icons/react/dist/csr/Info";
import { LaptopIcon as PhLaptop } from "@phosphor-icons/react/dist/csr/Laptop";
import { LayoutIcon as PhLayout } from "@phosphor-icons/react/dist/csr/Layout";
import { LightningIcon as PhLightning } from "@phosphor-icons/react/dist/csr/Lightning";
import { LinkBreakIcon as PhLinkBreak } from "@phosphor-icons/react/dist/csr/LinkBreak";
import { LinkSimpleIcon as PhLinkSimple } from "@phosphor-icons/react/dist/csr/LinkSimple";
import { ListBulletsIcon as PhListBullets } from "@phosphor-icons/react/dist/csr/ListBullets";
import { LockIcon as PhLock } from "@phosphor-icons/react/dist/csr/Lock";
import { LockSimpleIcon as PhLockSimple } from "@phosphor-icons/react/dist/csr/LockSimple";
import { MagnifyingGlassIcon as PhMagnifyingGlass } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { MegaphoneSimpleIcon as PhMegaphoneSimple } from "@phosphor-icons/react/dist/csr/MegaphoneSimple";
import { MinusIcon as PhMinus } from "@phosphor-icons/react/dist/csr/Minus";
import { MinusCircleIcon as PhMinusCircle } from "@phosphor-icons/react/dist/csr/MinusCircle";
import { MinusSquareIcon as PhMinusSquare } from "@phosphor-icons/react/dist/csr/MinusSquare";
import { MoonIcon as PhMoon } from "@phosphor-icons/react/dist/csr/Moon";
import { PaperPlaneRightIcon as PhPaperPlaneRight } from "@phosphor-icons/react/dist/csr/PaperPlaneRight";
import { PasswordIcon as PhPassword } from "@phosphor-icons/react/dist/csr/Password";
import { PauseIcon as PhPause } from "@phosphor-icons/react/dist/csr/Pause";
import { PencilSimpleIcon as PhPencilSimple } from "@phosphor-icons/react/dist/csr/PencilSimple";
import { PhoneIcon as PhPhone } from "@phosphor-icons/react/dist/csr/Phone";
import { PlayIcon as PhPlay } from "@phosphor-icons/react/dist/csr/Play";
import { PlayCircleIcon as PhPlayCircle } from "@phosphor-icons/react/dist/csr/PlayCircle";
import { PlusIcon as PhPlus } from "@phosphor-icons/react/dist/csr/Plus";
import { PlusCircleIcon as PhPlusCircle } from "@phosphor-icons/react/dist/csr/PlusCircle";
import { PlusSquareIcon as PhPlusSquare } from "@phosphor-icons/react/dist/csr/PlusSquare";
import { RadioButtonIcon as PhRadioButton } from "@phosphor-icons/react/dist/csr/RadioButton";
import { RectangleIcon as PhRectangle } from "@phosphor-icons/react/dist/csr/Rectangle";
import { RepeatIcon as PhRepeat } from "@phosphor-icons/react/dist/csr/Repeat";
import { RocketLaunchIcon as PhRocketLaunch } from "@phosphor-icons/react/dist/csr/RocketLaunch";
import { RowsIcon as PhRows } from "@phosphor-icons/react/dist/csr/Rows";
import { ShareNetworkIcon as PhShareNetwork } from "@phosphor-icons/react/dist/csr/ShareNetwork";
import { ShoppingCartIcon as PhShoppingCart } from "@phosphor-icons/react/dist/csr/ShoppingCart";
import { SidebarSimpleIcon as PhSidebarSimple } from "@phosphor-icons/react/dist/csr/SidebarSimple";
import { SignOutIcon as PhSignOut } from "@phosphor-icons/react/dist/csr/SignOut";
import { SmileyIcon as PhSmiley } from "@phosphor-icons/react/dist/csr/Smiley";
import { SparkleIcon as PhSparkle } from "@phosphor-icons/react/dist/csr/Sparkle";
import { SquareIcon as PhSquare } from "@phosphor-icons/react/dist/csr/Square";
import { StackIcon as PhStack } from "@phosphor-icons/react/dist/csr/Stack";
import { StarIcon as PhStar } from "@phosphor-icons/react/dist/csr/Star";
import { SunIcon as PhSun } from "@phosphor-icons/react/dist/csr/Sun";
import { TagIcon as PhTag } from "@phosphor-icons/react/dist/csr/Tag";
import { TargetIcon as PhTarget } from "@phosphor-icons/react/dist/csr/Target";
import { TextAaIcon as PhTextAa } from "@phosphor-icons/react/dist/csr/TextAa";
import { TextAlignCenterIcon as PhTextAlignCenter } from "@phosphor-icons/react/dist/csr/TextAlignCenter";
import { TextAlignLeftIcon as PhTextAlignLeft } from "@phosphor-icons/react/dist/csr/TextAlignLeft";
import { TextAlignRightIcon as PhTextAlignRight } from "@phosphor-icons/react/dist/csr/TextAlignRight";
import { TextBIcon as PhTextB } from "@phosphor-icons/react/dist/csr/TextB";
import { TextHOneIcon as PhTextHOne } from "@phosphor-icons/react/dist/csr/TextHOne";
import { TextItalicIcon as PhTextItalic } from "@phosphor-icons/react/dist/csr/TextItalic";
import { TextTIcon as PhTextT } from "@phosphor-icons/react/dist/csr/TextT";
import { TextUnderlineIcon as PhTextUnderline } from "@phosphor-icons/react/dist/csr/TextUnderline";
import { TicketIcon as PhTicket } from "@phosphor-icons/react/dist/csr/Ticket";
import { TrashIcon as PhTrash } from "@phosphor-icons/react/dist/csr/Trash";
import { TrendDownIcon as PhTrendDown } from "@phosphor-icons/react/dist/csr/TrendDown";
import { TrendUpIcon as PhTrendUp } from "@phosphor-icons/react/dist/csr/TrendUp";
import { TrophyIcon as PhTrophy } from "@phosphor-icons/react/dist/csr/Trophy";
import { UploadSimpleIcon as PhUploadSimple } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { UserIcon as PhUser } from "@phosphor-icons/react/dist/csr/User";
import { UserMinusIcon as PhUserMinus } from "@phosphor-icons/react/dist/csr/UserMinus";
import { UserPlusIcon as PhUserPlus } from "@phosphor-icons/react/dist/csr/UserPlus";
import { UsersIcon as PhUsers } from "@phosphor-icons/react/dist/csr/Users";
import { VideoCameraIcon as PhVideoCamera } from "@phosphor-icons/react/dist/csr/VideoCamera";
import { WalletIcon as PhWallet } from "@phosphor-icons/react/dist/csr/Wallet";
import { WarningIcon as PhWarning } from "@phosphor-icons/react/dist/csr/Warning";
import { WarningCircleIcon as PhWarningCircle } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { WebhooksLogoIcon as PhWebhooksLogo } from "@phosphor-icons/react/dist/csr/WebhooksLogo";
import { XIcon as PhX } from "@phosphor-icons/react/dist/csr/X";
import { XCircleIcon as PhXCircle } from "@phosphor-icons/react/dist/csr/XCircle";

import { createPhosphorMuiIcon } from "./createPhosphorMuiIcon";

export type { AppIconComponent, AppIconProps } from "./createPhosphorMuiIcon";
export { createPhosphorMuiIcon } from "./createPhosphorMuiIcon";

export const AccessTimeIcon = createPhosphorMuiIcon(PhClock, "AccessTimeIcon");
export const AccessTimeFilledIcon = createPhosphorMuiIcon(
  PhClock,
  "AccessTimeFilledIcon",
  "fill",
);
export const AddIcon = createPhosphorMuiIcon(PhPlus, "AddIcon");
export const AddBoxOutlinedIcon = createPhosphorMuiIcon(
  PhPlusSquare,
  "AddBoxOutlinedIcon",
);
export const ArchiveIcon = createPhosphorMuiIcon(
  PhArchive,
  "ArchiveIcon",
  "fill",
);
export const ArrowBackIcon = createPhosphorMuiIcon(
  PhArrowLeft,
  "ArrowBackIcon",
);
// Net-new Phosphor icon (no @mui/icons-material predecessor): named after the
// Phosphor glyph. Excluded from the parity story, which has no MUI side to pair.
export const ArrowCounterClockwiseIcon = createPhosphorMuiIcon(
  PhArrowCounterClockwise,
  "ArrowCounterClockwiseIcon",
);
export const ArrowDownwardIcon = createPhosphorMuiIcon(
  PhArrowDown,
  "ArrowDownwardIcon",
);
export const ArrowForwardIcon = createPhosphorMuiIcon(
  PhArrowRight,
  "ArrowForwardIcon",
);
export const ArrowUpwardIcon = createPhosphorMuiIcon(
  PhArrowUp,
  "ArrowUpwardIcon",
);
export const AttachMoneyIcon = createPhosphorMuiIcon(
  PhCurrencyDollar,
  "AttachMoneyIcon",
);
export const AutoAwesomeIcon = createPhosphorMuiIcon(
  PhSparkle,
  "AutoAwesomeIcon",
  "fill",
);
export const AutoAwesomeOutlinedIcon = createPhosphorMuiIcon(
  PhSparkle,
  "AutoAwesomeOutlinedIcon",
);
export const BarChartIcon = createPhosphorMuiIcon(
  PhChartBar,
  "BarChartIcon",
  "fill",
);
export const BoltIcon = createPhosphorMuiIcon(PhLightning, "BoltIcon", "fill");
export const BookOutlinedIcon = createPhosphorMuiIcon(
  PhBook,
  "BookOutlinedIcon",
);
export const BookmarkIcon = createPhosphorMuiIcon(
  PhBookmarkSimple,
  "BookmarkIcon",
);
export const CachedOutlinedIcon = createPhosphorMuiIcon(
  PhArrowsClockwise,
  "CachedOutlinedIcon",
);
export const CalendarMonthIcon = createPhosphorMuiIcon(
  PhCalendarDots,
  "CalendarMonthIcon",
  "fill",
);
export const CalendarTodayIcon = createPhosphorMuiIcon(
  PhCalendarBlank,
  "CalendarTodayIcon",
  "fill",
);
export const CalendarTodayOutlinedIcon = createPhosphorMuiIcon(
  PhCalendarBlank,
  "CalendarTodayOutlinedIcon",
);
export const CallSplitIcon = createPhosphorMuiIcon(
  PhArrowsSplit,
  "CallSplitIcon",
);
export const CancelIcon = createPhosphorMuiIcon(
  PhXCircle,
  "CancelIcon",
  "fill",
);
// Net-new Phosphor icon (no @mui/icons-material predecessor).
export const CaretDownIcon = createPhosphorMuiIcon(
  PhCaretDown,
  "CaretDownIcon",
);
export const CheckIcon = createPhosphorMuiIcon(PhCheck, "CheckIcon");
export const CheckBoxIcon = createPhosphorMuiIcon(
  PhCheckSquare,
  "CheckBoxIcon",
  "fill",
);
export const CheckBoxOutlineBlankIcon = createPhosphorMuiIcon(
  PhSquare,
  "CheckBoxOutlineBlankIcon",
);
export const CheckCircleIcon = createPhosphorMuiIcon(
  PhCheckCircle,
  "CheckCircleIcon",
  "fill",
);
export const CheckCircleOutlineIcon = createPhosphorMuiIcon(
  PhCheckCircle,
  "CheckCircleOutlineIcon",
);
export const CheckCircleOutlineOutlinedIcon = createPhosphorMuiIcon(
  PhCheckCircle,
  "CheckCircleOutlineOutlinedIcon",
);
export const ChevronLeftIcon = createPhosphorMuiIcon(
  PhCaretLeft,
  "ChevronLeftIcon",
);
export const ChevronRightIcon = createPhosphorMuiIcon(
  PhCaretRight,
  "ChevronRightIcon",
);
export const CircleIcon = createPhosphorMuiIcon(PhCircle, "CircleIcon", "fill");
export const CloseIcon = createPhosphorMuiIcon(PhX, "CloseIcon");
export const CloseOutlinedIcon = createPhosphorMuiIcon(
  PhX,
  "CloseOutlinedIcon",
);
export const CloseRoundedIcon = createPhosphorMuiIcon(PhX, "CloseRoundedIcon");
export const CloudUploadIcon = createPhosphorMuiIcon(
  PhCloudArrowUp,
  "CloudUploadIcon",
  "fill",
);
export const CloudUploadOutlinedIcon = createPhosphorMuiIcon(
  PhCloudArrowUp,
  "CloudUploadOutlinedIcon",
);
export const ContentCopyIcon = createPhosphorMuiIcon(PhCopy, "ContentCopyIcon");
export const ContentPasteIcon = createPhosphorMuiIcon(
  PhClipboard,
  "ContentPasteIcon",
  "fill",
);
// Net-new Phosphor icon (no @mui/icons-material predecessor).
export const CrownIcon = createPhosphorMuiIcon(PhCrown, "CrownIcon");
export const DarkModeOutlinedIcon = createPhosphorMuiIcon(
  PhMoon,
  "DarkModeOutlinedIcon",
);
export const DataObjectIcon = createPhosphorMuiIcon(
  PhBracketsCurly,
  "DataObjectIcon",
);
export const DeleteIcon = createPhosphorMuiIcon(PhTrash, "DeleteIcon", "fill");
export const DeleteOutlineIcon = createPhosphorMuiIcon(
  PhTrash,
  "DeleteOutlineIcon",
);
export const DescriptionIcon = createPhosphorMuiIcon(
  PhFileText,
  "DescriptionIcon",
  "fill",
);
export const DescriptionOutlinedIcon = createPhosphorMuiIcon(
  PhFileText,
  "DescriptionOutlinedIcon",
);
export const DownloadIcon = createPhosphorMuiIcon(
  PhDownloadSimple,
  "DownloadIcon",
);
export const DragIndicatorIcon = createPhosphorMuiIcon(
  PhDotsSixVertical,
  "DragIndicatorIcon",
);
export const EditIcon = createPhosphorMuiIcon(
  PhPencilSimple,
  "EditIcon",
  "fill",
);
export const EditOutlinedIcon = createPhosphorMuiIcon(
  PhPencilSimple,
  "EditOutlinedIcon",
);
export const EmailIcon = createPhosphorMuiIcon(
  PhEnvelopeSimple,
  "EmailIcon",
  "fill",
);
export const EmailOutlinedIcon = createPhosphorMuiIcon(
  PhEnvelopeSimple,
  "EmailOutlinedIcon",
);
export const EmojiEventsIcon = createPhosphorMuiIcon(
  PhTrophy,
  "EmojiEventsIcon",
  "fill",
);
export const ErrorIcon = createPhosphorMuiIcon(
  PhWarningCircle,
  "ErrorIcon",
  "fill",
);
export const ErrorOutlineIcon = createPhosphorMuiIcon(
  PhWarningCircle,
  "ErrorOutlineIcon",
);
export const ExpandLessIcon = createPhosphorMuiIcon(
  PhCaretUp,
  "ExpandLessIcon",
);
export const ExpandMoreIcon = createPhosphorMuiIcon(
  PhCaretDown,
  "ExpandMoreIcon",
);
export const EyedropperIcon = createPhosphorMuiIcon(
  PhEyedropper,
  "EyedropperIcon",
);
export const FavoriteIcon = createPhosphorMuiIcon(
  PhHeart,
  "FavoriteIcon",
  "fill",
);
export const FileDownloadIcon = createPhosphorMuiIcon(
  PhDownloadSimple,
  "FileDownloadIcon",
);
export const FileUploadIcon = createPhosphorMuiIcon(
  PhUploadSimple,
  "FileUploadIcon",
);
export const FileUploadOutlinedIcon = createPhosphorMuiIcon(
  PhUploadSimple,
  "FileUploadOutlinedIcon",
);
export const FirstPageIcon = createPhosphorMuiIcon(
  PhCaretLineLeft,
  "FirstPageIcon",
);
export const FormatAlignCenterIcon = createPhosphorMuiIcon(
  PhTextAlignCenter,
  "FormatAlignCenterIcon",
);
export const FormatAlignLeftIcon = createPhosphorMuiIcon(
  PhTextAlignLeft,
  "FormatAlignLeftIcon",
);
export const FormatAlignRightIcon = createPhosphorMuiIcon(
  PhTextAlignRight,
  "FormatAlignRightIcon",
);
export const FormatBoldIcon = createPhosphorMuiIcon(PhTextB, "FormatBoldIcon");
export const FormatItalicIcon = createPhosphorMuiIcon(
  PhTextItalic,
  "FormatItalicIcon",
);
export const FormatListBulletedIcon = createPhosphorMuiIcon(
  PhListBullets,
  "FormatListBulletedIcon",
);
export const FormatUnderlinedIcon = createPhosphorMuiIcon(
  PhTextUnderline,
  "FormatUnderlinedIcon",
);
export const GrainIcon = createPhosphorMuiIcon(PhDotsNine, "GrainIcon");
export const GridViewIcon = createPhosphorMuiIcon(PhGridFour, "GridViewIcon");
export const HighlightOffIcon = createPhosphorMuiIcon(
  PhXCircle,
  "HighlightOffIcon",
);
export const HighlightOffOutlinedIcon = createPhosphorMuiIcon(
  PhXCircle,
  "HighlightOffOutlinedIcon",
);
export const HistoryIcon = createPhosphorMuiIcon(
  PhClockCounterClockwise,
  "HistoryIcon",
);
export const HomeIcon = createPhosphorMuiIcon(PhHouse, "HomeIcon", "fill");
export const ImageNotSupportedOutlinedIcon = createPhosphorMuiIcon(
  PhImageBroken,
  "ImageNotSupportedOutlinedIcon",
);
export const ImageOutlinedIcon = createPhosphorMuiIcon(
  PhImage,
  "ImageOutlinedIcon",
);
export const InfoIcon = createPhosphorMuiIcon(PhInfo, "InfoIcon", "fill");
export const InfoOutlineIcon = createPhosphorMuiIcon(PhInfo, "InfoOutlineIcon");
export const InfoOutlinedIcon = createPhosphorMuiIcon(
  PhInfo,
  "InfoOutlinedIcon",
);
export const InsertDriveFileOutlinedIcon = createPhosphorMuiIcon(
  PhFile,
  "InsertDriveFileOutlinedIcon",
);
export const KeyboardArrowDownIcon = createPhosphorMuiIcon(
  PhCaretDown,
  "KeyboardArrowDownIcon",
);
export const KeyboardArrowLeftIcon = createPhosphorMuiIcon(
  PhCaretLeft,
  "KeyboardArrowLeftIcon",
);
export const KeyboardArrowRightIcon = createPhosphorMuiIcon(
  PhCaretRight,
  "KeyboardArrowRightIcon",
);
export const LanguageIcon = createPhosphorMuiIcon(PhGlobe, "LanguageIcon");
export const LastPageIcon = createPhosphorMuiIcon(
  PhCaretLineRight,
  "LastPageIcon",
);
export const LibraryBooksIcon = createPhosphorMuiIcon(
  PhBooks,
  "LibraryBooksIcon",
);
export const LightModeOutlinedIcon = createPhosphorMuiIcon(
  PhSun,
  "LightModeOutlinedIcon",
);
export const LinkIcon = createPhosphorMuiIcon(PhLinkSimple, "LinkIcon");
export const LinkOffIcon = createPhosphorMuiIcon(PhLinkBreak, "LinkOffIcon");
export const LockIcon = createPhosphorMuiIcon(PhLock, "LockIcon", "fill");
export const LocalFireDepartmentRoundedIcon = createPhosphorMuiIcon(
  PhFire,
  "LocalFireDepartmentRoundedIcon",
  "fill",
);
export const LogoutIcon = createPhosphorMuiIcon(PhSignOut, "LogoutIcon");
export const LockSimpleIcon = createPhosphorMuiIcon(
  PhLockSimple,
  "LockSimpleIcon",
);
export const MoreVertIcon = createPhosphorMuiIcon(
  PhDotsThreeVertical,
  "MoreVertIcon",
  "bold",
);
export const NavigateNextIcon = createPhosphorMuiIcon(
  PhCaretRight,
  "NavigateNextIcon",
);
export const NotificationsIcon = createPhosphorMuiIcon(
  PhBell,
  "NotificationsIcon",
);
export const OpenInNewIcon = createPhosphorMuiIcon(
  PhArrowSquareOut,
  "OpenInNewIcon",
);
export const OpenInNewOutlinedIcon = createPhosphorMuiIcon(
  PhArrowSquareOut,
  "OpenInNewOutlinedIcon",
);
export const PeopleAltOutlinedIcon = createPhosphorMuiIcon(
  PhUsers,
  "PeopleAltOutlinedIcon",
);
export const PeopleIcon = createPhosphorMuiIcon(PhUsers, "PeopleIcon", "fill");
export const PeopleOutlineIcon = createPhosphorMuiIcon(
  PhUsers,
  "PeopleOutlineIcon",
);
export const PersonIcon = createPhosphorMuiIcon(PhUser, "PersonIcon", "fill");
export const PersonAddIcon = createPhosphorMuiIcon(
  PhUserPlus,
  "PersonAddIcon",
  "fill",
);
export const PersonOffIcon = createPhosphorMuiIcon(
  PhUserMinus,
  "PersonOffIcon",
  "fill",
);
// Net-new Phosphor icon (no @mui/icons-material predecessor).
export const PauseIcon = createPhosphorMuiIcon(PhPause, "PauseIcon", "fill");
export const PlayArrowIcon = createPhosphorMuiIcon(
  PhPlay,
  "PlayArrowIcon",
  "fill",
);
export const PlayArrowRoundedIcon = createPhosphorMuiIcon(
  PhPlay,
  "PlayArrowRoundedIcon",
  "fill",
);
export const PlayCircleIcon = createPhosphorMuiIcon(
  PhPlayCircle,
  "PlayCircleIcon",
  "fill",
);
export const RadioButtonCheckedIcon = createPhosphorMuiIcon(
  PhRadioButton,
  "RadioButtonCheckedIcon",
  "fill",
);
export const RefreshIcon = createPhosphorMuiIcon(
  PhArrowClockwise,
  "RefreshIcon",
);
export const RemoveIcon = createPhosphorMuiIcon(PhMinus, "RemoveIcon");
export const RemoveCircleOutlineIcon = createPhosphorMuiIcon(
  PhMinusCircle,
  "RemoveCircleOutlineIcon",
);
export const AddCircleOutlineIcon = createPhosphorMuiIcon(
  PhPlusCircle,
  "AddCircleOutlineIcon",
);
export const DiceIcon = createPhosphorMuiIcon(PhDiceFive, "DiceIcon");
export const FlaskIcon = createPhosphorMuiIcon(PhFlask, "FlaskIcon");
export const RocketLaunchIcon = createPhosphorMuiIcon(
  PhRocketLaunch,
  "RocketLaunchIcon",
  "fill",
);
export const RestoreIcon = createPhosphorMuiIcon(
  PhClockCounterClockwise,
  "RestoreIcon",
);
export const SaveIcon = createPhosphorMuiIcon(PhFloppyDisk, "SaveIcon", "fill");
export const ScheduleIcon = createPhosphorMuiIcon(PhClock, "ScheduleIcon");
export const SearchIcon = createPhosphorMuiIcon(
  PhMagnifyingGlass,
  "SearchIcon",
);
export const SendIcon = createPhosphorMuiIcon(PhPaperPlaneRight, "SendIcon");
export const SettingsIcon = createPhosphorMuiIcon(
  PhGear,
  "SettingsIcon",
  "fill",
);
export const SettingsOutlinedIcon = createPhosphorMuiIcon(
  PhGear,
  "SettingsOutlinedIcon",
);
export const ShareIcon = createPhosphorMuiIcon(PhShareNetwork, "ShareIcon");
export const SmsIcon = createPhosphorMuiIcon(PhChatDots, "SmsIcon", "fill");
export const AppleLogoIcon = createPhosphorMuiIcon(
  PhAppleLogo,
  "AppleLogoIcon",
  "fill",
);
export const AndroidLogoIcon = createPhosphorMuiIcon(
  PhAndroidLogo,
  "AndroidLogoIcon",
  "fill",
);
export const SmsOutlinedIcon = createPhosphorMuiIcon(
  PhChatDots,
  "SmsOutlinedIcon",
);
export const StarRoundedIcon = createPhosphorMuiIcon(
  PhStar,
  "StarRoundedIcon",
  "fill",
);
export const StackIcon = createPhosphorMuiIcon(PhStack, "StackIcon");
export const TrendingUpIcon = createPhosphorMuiIcon(
  PhTrendUp,
  "TrendingUpIcon",
);
export const TuneIcon = createPhosphorMuiIcon(PhFadersHorizontal, "TuneIcon");
export const UnfoldLessIcon = createPhosphorMuiIcon(
  PhArrowsInLineVertical,
  "UnfoldLessIcon",
);
export const UnfoldMoreIcon = createPhosphorMuiIcon(
  PhArrowsOutLineVertical,
  "UnfoldMoreIcon",
);
export const VideocamIcon = createPhosphorMuiIcon(
  PhVideoCamera,
  "VideocamIcon",
  "fill",
);
export const ViewColumnIcon = createPhosphorMuiIcon(
  PhColumns,
  "ViewColumnIcon",
  "fill",
);
export const ViewCompactOutlinedIcon = createPhosphorMuiIcon(
  PhLayout,
  "ViewCompactOutlinedIcon",
);
export const ViewListIcon = createPhosphorMuiIcon(PhRows, "ViewListIcon");
export const WarningIcon = createPhosphorMuiIcon(
  PhWarning,
  "WarningIcon",
  "fill",
);
export const WarningAmberIcon = createPhosphorMuiIcon(
  PhWarning,
  "WarningAmberIcon",
);
export const WarningAmberOutlinedIcon = createPhosphorMuiIcon(
  PhWarning,
  "WarningAmberOutlinedIcon",
);
export const WarningAmberRoundedIcon = createPhosphorMuiIcon(
  PhWarning,
  "WarningAmberRoundedIcon",
);
export const WhatshotIcon = createPhosphorMuiIcon(
  PhFire,
  "WhatshotIcon",
  "fill",
);

// Glyphs added for the Orion icon migration (src/orion/OrionIcons/*). Some have
// an MUI-equivalent name (used here for forward-compat with the codemod), others
// are named after their Phosphor glyph where no MUI predecessor exists.
export const AccountBalanceWalletIcon = createPhosphorMuiIcon(
  PhWallet,
  "AccountBalanceWalletIcon",
);
export const AdsClickIcon = createPhosphorMuiIcon(
  PhCursorClick,
  "AdsClickIcon",
);
export const ArrowClockwiseIcon = createPhosphorMuiIcon(
  PhArrowClockwise,
  "ArrowClockwiseIcon",
);
export const CampaignIcon = createPhosphorMuiIcon(
  PhMegaphoneSimple,
  "CampaignIcon",
);
export const CodeIcon = createPhosphorMuiIcon(PhCode, "CodeIcon");
export const CropFreeIcon = createPhosphorMuiIcon(
  PhFrameCorners,
  "CropFreeIcon",
);
export const DraftsIcon = createPhosphorMuiIcon(PhEnvelopeOpen, "DraftsIcon");
export const GpsFixedIcon = createPhosphorMuiIcon(PhTarget, "GpsFixedIcon");
export const HexagonIcon = createPhosphorMuiIcon(PhHexagon, "HexagonIcon");
export const IndeterminateCheckBoxIcon = createPhosphorMuiIcon(
  PhMinusSquare,
  "IndeterminateCheckBoxIcon",
);
export const LaptopIcon = createPhosphorMuiIcon(PhLaptop, "LaptopIcon");
export const MoodIcon = createPhosphorMuiIcon(PhSmiley, "MoodIcon");
export const RepeatIcon = createPhosphorMuiIcon(PhRepeat, "RepeatIcon");
export const SmartphoneIcon = createPhosphorMuiIcon(
  PhDeviceMobile,
  "SmartphoneIcon",
);
export const ShoppingCartIcon = createPhosphorMuiIcon(
  PhShoppingCart,
  "ShoppingCartIcon",
);
export const TagIcon = createPhosphorMuiIcon(PhTag, "TagIcon");
export const TrendingDownIcon = createPhosphorMuiIcon(
  PhTrendDown,
  "TrendingDownIcon",
);
export const VisibilityOffOutlinedIcon = createPhosphorMuiIcon(
  PhEyeSlash,
  "VisibilityOffOutlinedIcon",
);
export const VisibilityOutlinedIcon = createPhosphorMuiIcon(
  PhEye,
  "VisibilityOutlinedIcon",
);
export const WebhookIcon = createPhosphorMuiIcon(PhWebhooksLogo, "WebhookIcon");

// Glyphs for the Optin editor block palette (GrapesJS block media). Named after
// their Phosphor glyph — no MUI predecessor exists for them.
export const AppWindowIcon = createPhosphorMuiIcon(
  PhAppWindow,
  "AppWindowIcon",
);
export const ArticleIcon = createPhosphorMuiIcon(PhArticle, "ArticleIcon");
export const BoundingBoxIcon = createPhosphorMuiIcon(
  PhBoundingBox,
  "BoundingBoxIcon",
);
export const CursorTextIcon = createPhosphorMuiIcon(
  PhCursorText,
  "CursorTextIcon",
);
export const GoogleLogoIcon = createPhosphorMuiIcon(
  PhGoogleLogo,
  "GoogleLogoIcon",
);
export const ImageSquareIcon = createPhosphorMuiIcon(
  PhImageSquare,
  "ImageSquareIcon",
);
export const LayoutIcon = createPhosphorMuiIcon(PhLayout, "LayoutIcon");
export const PasswordIcon = createPhosphorMuiIcon(PhPassword, "PasswordIcon");
export const PhoneIcon = createPhosphorMuiIcon(PhPhone, "PhoneIcon");
export const RectangleIcon = createPhosphorMuiIcon(
  PhRectangle,
  "RectangleIcon",
);
export const SquareIcon = createPhosphorMuiIcon(PhSquare, "SquareIcon");
export const TextAaIcon = createPhosphorMuiIcon(PhTextAa, "TextAaIcon");
export const TextHOneIcon = createPhosphorMuiIcon(PhTextHOne, "TextHOneIcon");
export const TextTIcon = createPhosphorMuiIcon(PhTextT, "TextTIcon");
export const TicketIcon = createPhosphorMuiIcon(PhTicket, "TicketIcon");

export const SidebarSimpleIcon = createPhosphorMuiIcon(
  PhSidebarSimple,
  "SidebarSimpleIcon",
);

// Marks a merge-tag chip whose value is generated content from the tile's
// blueprint. Named after the Phosphor glyph — no MUI predecessor exists.
export const BlueprintIcon = createPhosphorMuiIcon(
  PhBlueprint,
  "BlueprintIcon",
);

export { OrionGenericCardIcon } from "@orion/OrionIcons/OrionGenericCardIcon";
export { OrionMastercardIcon } from "@orion/OrionIcons/OrionMastercardIcon";
export { OrionVisaIcon } from "@orion/OrionIcons/OrionVisaIcon";
